import { Badge, Banner, Button, Field, Input } from '@cloudflare/kumo';
import { SidePanel } from './ui/SidePanel';
import { useCallback, useEffect, useState } from 'react';
import { BrandLogo } from './BrandLogo';
import { PasswordField } from './ui/PasswordField';
import { ICONS } from './ui/icons';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';
import { ProviderLogo } from './ProviderLogo';

interface MetaConfig {
  appId: string; configId: string; graphVersion: string; hasSecret: boolean; verifyToken: string; webhookUrl: string; configured: boolean;
  igAppId: string; igHasSecret: boolean; igSecretInvalid?: boolean; igWebhookUrl: string; igRedirectUri: string;
  leadsConfigId: string; leadsWebhookUrl: string; leadsRedirectUri: string;
}

/** Plataforma › Integrações: app da Meta (Tech Provider) para o Embedded Signup e o webhook */
export function MetaAppCard() {
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [cfg, setCfg] = useState<MetaConfig | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ appId: '', configId: '', appSecret: '', graphVersion: '', igAppId: '', igAppSecret: '', leadsConfigId: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await fetchApi('/api/tools/meta-config');
    if (res.ok) setCfg(await res.json());
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetchApi('/api/tools/meta-config', { method: 'PUT', body: JSON.stringify(form) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Erro ao salvar');
      setCfg(body);
      toast.success('App da Meta salvo');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); toast.success('Copiado'); } catch { /* sem permissão */ }
  };

  return (
    <>
      <div className="settings-card">
        <div>
          <div className="flex items-center justify-between gap-2">
            <span style={{ height: 28, display: 'flex', alignItems: 'center' }}><ProviderLogo id="meta" size={28} /></span>
            {cfg?.configured ? <Badge variant="green">Configurado</Badge> : <Badge variant="neutral">Não configurado</Badge>}
          </div>
          <div className="font-semibold" style={{ marginTop: 12 }}>API oficial da Meta</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Tech Provider: Embedded Signup e webhook</div>
          <div className="integration-host" title={cfg?.appId || undefined}>
            <ICONS.Key size={16} />
            <span>{cfg?.appId ? `App ${cfg.appId} · ${cfg.graphVersion}` : 'sem app'}</span>
          </div>
        </div>
        <div className="settings-card__actions">
          <Button variant="secondary" icon={cfg?.configured ? ICONS.Edit : ICONS.Plus} onClick={() => { setForm({ appId: cfg?.appId || '', configId: cfg?.configId || '', appSecret: '', graphVersion: cfg?.graphVersion || 'v25.0', igAppId: cfg?.igAppId || '', igAppSecret: '', leadsConfigId: cfg?.leadsConfigId || '' }); setOpen(true); }}>
            {cfg?.configured ? 'Editar' : 'Configurar'}
          </Button>
        </div>
      </div>

      <SidePanel open={open} onClose={() => setOpen(false)} title="App da Meta (Tech Provider)" description="Em developers.facebook.com, no seu app: ID do app e chave secreta (Configurações › Básico) e o ID da configuração do Login do Facebook para Empresas criada para o WhatsApp Embedded Signup." formId="meta-form" submitting={saving} submitDisabled={!form.appId || !form.configId || (!cfg?.hasSecret && !form.appSecret)}>
        <form id="meta-form" noValidate onSubmit={save} className="flex flex-col gap-4">
          <div className="flex flex-col gap-4">
            <Field label="ID do app">
              <Input onChange={(e) => setForm({ ...form, appId: e.target.value.trim() })} value={form.appId} />
            </Field>
            <Field label="ID da configuração (Embedded Signup)">
              <Input onChange={(e) => setForm({ ...form, configId: e.target.value.trim() })} value={form.configId} />
            </Field>
            <PasswordField label="Chave secreta do app" placeholder={cfg?.hasSecret ? 'Deixe em branco para manter a atual' : ''} value={form.appSecret}
              onChange={(value) => setForm({ ...form, appSecret: value.trim() })} autoComplete="off" />
            <Field label="Versão da Graph API">
              <Input onChange={(e) => setForm({ ...form, graphVersion: e.target.value.trim() })} value={form.graphVersion} />
            </Field>
          </div>

          {cfg?.verifyToken && (
            <div className="meta-webhook">
              <div className="font-semibold flex items-center gap-2"><BrandLogo brand="whatsapp" size={18} rounded={4} /> Webhook do WhatsApp no app da Meta</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Em WhatsApp › Configuração, use estes dados e assine os campos <strong>messages</strong> e, para a coexistência com o app, <strong>history</strong>, <strong>smb_app_state_sync</strong> e <strong>smb_message_echoes</strong>.</div>
              <div className="flex gap-2 items-end" style={{ marginTop: 8 }}>
                <div style={{ flex: 1 }}>
                  <Field label="URL de retorno">
                    <Input  value={cfg.webhookUrl} readOnly onFocus={(e) => e.currentTarget.select()} />
                  </Field>
                </div>
                <Button variant="secondary" icon={ICONS.Copy} type="button" onClick={() => copy(cfg.webhookUrl)}>Copiar</Button>
              </div>
              <div className="flex gap-2 items-end" style={{ marginTop: 8 }}>
                <div style={{ flex: 1 }}>
                  <Field label="Token de verificação">
                    <Input  value={cfg.verifyToken} readOnly onFocus={(e) => e.currentTarget.select()} />
                  </Field>
                </div>
                <Button variant="secondary" icon={ICONS.Copy} type="button" onClick={() => copy(cfg.verifyToken)}>Copiar</Button>
              </div>
            </div>
          )}

          <div className="meta-webhook">
            <div className="font-semibold flex items-center gap-2"><BrandLogo brand="instagram" size={18} rounded={4} /> Instagram (API com login do Instagram)</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              No mesmo app, em Instagram › Configuração da API com login do Instagram: o <strong>ID do app do Instagram</strong> e a
              <strong> chave secreta do app do Instagram</strong>. Cadastre a URL de redirecionamento e o webhook abaixo (campos
              <strong> messages</strong>, <strong>messaging_postbacks</strong> e <strong>comments</strong>) com o mesmo token de verificação.
            </div>
            {cfg?.igSecretInvalid && (
              <div style={{ marginTop: 8 }}>
                <Banner variant="error" description={<>
                  A chave secreta do app do Instagram gravada não é válida (deve ter 32 caracteres; parece um token de acesso). Cole a chave correta abaixo e salve: sem ela a conexão do Instagram falha.
                </>} />
              </div>
            )}
            <div className="flex flex-col gap-4" style={{ marginTop: 8 }}>
              <Field label="ID do app do Instagram">
                <Input onChange={(e) => setForm({ ...form, igAppId: e.target.value.trim() })} value={form.igAppId} />
              </Field>
              <PasswordField label="Chave secreta do app do Instagram" placeholder={cfg?.igHasSecret ? 'Deixe em branco para manter a atual' : ''} value={form.igAppSecret}
                onChange={(value) => setForm({ ...form, igAppSecret: value.trim() })} autoComplete="off" />
            </div>
            {cfg?.igWebhookUrl && (
              <>
                <div className="flex gap-2 items-end" style={{ marginTop: 8 }}>
                  <div style={{ flex: 1 }}>
                    <Field label="URL de redirecionamento do OAuth">
                      <Input  value={cfg.igRedirectUri} readOnly onFocus={(e) => e.currentTarget.select()} />
                    </Field>
                  </div>
                  <Button variant="secondary" icon={ICONS.Copy} type="button" onClick={() => copy(cfg.igRedirectUri)}>Copiar</Button>
                </div>
                <div className="flex gap-2 items-end" style={{ marginTop: 8 }}>
                  <div style={{ flex: 1 }}>
                    <Field label="URL do webhook do Instagram">
                      <Input  value={cfg.igWebhookUrl} readOnly onFocus={(e) => e.currentTarget.select()} />
                    </Field>
                  </div>
                  <Button variant="secondary" icon={ICONS.Copy} type="button" onClick={() => copy(cfg.igWebhookUrl)}>Copiar</Button>
                </div>
              </>
            )}
          </div>

          <div className="meta-webhook">
            <div className="font-semibold flex items-center gap-2"><BrandLogo brand="facebook" size={18} rounded={4} /> Leads do Facebook (Lead Ads)</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              Permissões <strong>leads_retrieval</strong>, <strong>pages_show_list</strong>, <strong>pages_manage_metadata</strong> e
              <strong> pages_read_engagement</strong>. Se o app usa Login do Facebook para Empresas, crie uma configuração com elas e informe o ID.
              No produto Webhooks › Página, assine o campo <strong>leadgen</strong> com a URL abaixo e o mesmo token.
            </div>
            <div className="flex flex-col gap-4" style={{ marginTop: 8 }}>
              <Field label="ID da configuração do login (Lead Ads, opcional)">
                <Input onChange={(e) => setForm({ ...form, leadsConfigId: e.target.value.trim() })} value={form.leadsConfigId} />
              </Field>
            </div>
            {cfg?.leadsWebhookUrl && (
              <>
                <div className="flex gap-2 items-end" style={{ marginTop: 8 }}>
                  <div style={{ flex: 1 }}>
                    <Field label="URL de redirecionamento do login">
                      <Input  value={cfg.leadsRedirectUri} readOnly onFocus={(e) => e.currentTarget.select()} />
                    </Field>
                  </div>
                  <Button variant="secondary" icon={ICONS.Copy} type="button" onClick={() => copy(cfg.leadsRedirectUri)}>Copiar</Button>
                </div>
                <div className="flex gap-2 items-end" style={{ marginTop: 8 }}>
                  <div style={{ flex: 1 }}>
                    <Field label="URL do webhook da página">
                      <Input  value={cfg.leadsWebhookUrl} readOnly onFocus={(e) => e.currentTarget.select()} />
                    </Field>
                  </div>
                  <Button variant="secondary" icon={ICONS.Copy} type="button" onClick={() => copy(cfg.leadsWebhookUrl)}>Copiar</Button>
                </div>
              </>
            )}
          </div>
        </form>
      </SidePanel>
    </>
  );
}
